/**
 * Transfer group template actions renderer
 */
import {
  Component,
  ViewEncapsulation,
  Injector,
  Type,
  InjectionToken,
  AbstractType,
} from '@angular/core';
import { ICellRendererParams } from 'ag-grid-community';
import { AgRendererComponent } from 'ag-grid-angular';
import {
  ActionButton,
  ITransferData,
  COMMON_ACTION_TYPES,
  IModalDialogResult,
  ENTITY_TYPES,
} from '@shared/types';
import { Router } from '@angular/router';
import { get, isFunction, forEach, cloneDeep } from 'lodash';
import { StateService, TransferGroupTemplateService } from '@shared/services';
import { APIActions, ToolsActions, UiActions } from '@store/actions';
import { lazyInject } from '@containers/base/decorators/lazy-inject.decorator';
import { TRANSFER_GROUP_TEMPLATE_ACTION_BUTTONS } from '@app/shared/types/entities/transfer-group-template/transfer-group-template-acition-button.enums';
import { removeTemplateModalOptions } from '@app/shared/services/transfer-group-template/remove-template-modal.defination';

// eslint-disable-next-line @angular-eslint/prefer-on-push-component-change-detection
@Component({
  selector: 'xvi-transfer-template-actions-renderer',
  templateUrl: 'transfer-group-template-actions-render.component.html',
  styleUrls: ['transfer-group-template-actions-render.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class XVITransferGroupTemplateRendererComponent
  implements AgRendererComponent
{
  @lazyInject(Router) public router: Router;
  @lazyInject(UiActions) public uiActions: UiActions;
  @lazyInject(ToolsActions) public toolsActions: ToolsActions;
  @lazyInject(TransferGroupTemplateService)
  public transferGroupTemplateService: TransferGroupTemplateService;
  @lazyInject(StateService) public stateService: StateService;
  @lazyInject(APIActions) public apiActions: APIActions;

  public data: ITransferData;
  public actionButtons: ActionButton[];

  public constructor(public injector: Injector) {}

  public agInit(params: ICellRendererParams): void {
    this.data = params.node.data;
    this.initActionButtons();
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  public refresh(params: ICellRendererParams): boolean {
    const defaultRequestParams = {
      query: {},
      options: {},
    };
    const entityType = this.transferGroupTemplateService.config.ENTITY_TYPE;
    const previousRequest = this.stateService.getByPath(
      ['entities', entityType, 'previousRequest'],
      defaultRequestParams,
    );

    this.apiActions.fetchList(
      entityType,
      previousRequest.query,
      previousRequest.options,
    );

    return true;
  }

  public emitAction(type: COMMON_ACTION_TYPES): void {
    switch (type) {
      case COMMON_ACTION_TYPES.Edit:
        this.edit();
        break;
      case COMMON_ACTION_TYPES.Remove:
        this.handleItemRemove();
        break;
      default:
        break;
    }
  }

  public inject<T>(
    token: Type<T> | InjectionToken<T> | AbstractType<T>,
    notFoundValue?: T,
  ): T {
    if (isFunction(this.injector.get)) {
      return this.injector?.get(token, notFoundValue);
    }

    return notFoundValue;
  }

  protected initActionButtons(): void {
    const entityType = ENTITY_TYPES.TRANSFER_GROUP_TEMPLATE;
    const canEdit =
      this.transferGroupTemplateService.permissionService.canManage(entityType);
    const canDelete =
      this.transferGroupTemplateService.permissionService.canDelete(entityType);
    this.actionButtons = cloneDeep(
      TRANSFER_GROUP_TEMPLATE_ACTION_BUTTONS,
    ) as ActionButton[];

    forEach(this.actionButtons, (btn) => {
      switch (btn.type) {
        case COMMON_ACTION_TYPES.Edit:
          btn.visible = canEdit;
          break;
        case COMMON_ACTION_TYPES.Remove:
          btn.visible = canDelete;
          break;
        default:
          break;
      }
    });
  }

  public edit(): void {
    this.router.navigate([
      this.transferGroupTemplateService.config.uiRoute,
      get(this, 'data._id', ''),
    ]);
  }

  public async handleItemRemove(): Promise<void> {
    const templateId = get(this, ['data', '_id']);
    const { confirmed }: IModalDialogResult =
      await this.uiActions.promptFormModal(
        removeTemplateModalOptions({ isMultiSelect: false }),
      );

    if (confirmed) {
      try {
        this.uiActions.enableLoader();
        await this.transferGroupTemplateService
          .deleteItems(templateId)
          .toPromise();
        this.refresh({} as ICellRendererParams);
      } catch (error) {
      } finally {
        this.uiActions.disableLoader();
      }
    }
  }
}
